import React, { FC, useCallback, useMemo, useState } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { CirclePlus } from 'lucide-react';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import { Button } from 'components/ui/button';
import UploadImage from 'components/UploadImage/UploadImage';

import { StoreThemeFormValues, TabsItem } from '../../StoreTheme/storeTheme.types';

import { FormField } from './FormField';

export type StoreTabsFormValues = Pick<StoreThemeFormValues, 'tabs'>;

const emptyDraft = {
  name: '',
  publisherTabId: '',
  iconUrl: ''
};

export const TabsSectionFields: FC = () => {
  const { values, setFieldValue } = useFormikContext<StoreTabsFormValues>();
  const [draft, setDraft] = useState(emptyDraft);
  const [editedTabId, setEditedTabId] = useState('');

  const tableRows = useMemo(
    () => (values.tabs || []).filter((t) => !t.toBeDeleted),
    [values.tabs]
  );

  const nameError = useMemo(() => {
    if (
      draft.name &&
      tableRows.some(
        (item) => item.publisherTabId !== editedTabId && item.name === draft.name
      )
    ) {
      return 'Tab name already exists';
    }
    return '';
  }, [draft.name, tableRows, editedTabId]);

  const idError = useMemo(() => {
    if (draft.publisherTabId?.includes(' ')) {
      return 'Tab ID should not contain spaces';
    }
    if (
      draft.publisherTabId &&
      draft.publisherTabId !== editedTabId &&
      tableRows.some((item) => item.publisherTabId === draft.publisherTabId)
    ) {
      return 'Tab ID already exists';
    }
    return '';
  }, [draft.publisherTabId, tableRows, editedTabId]);

  const addOrSaveTab = useCallback(() => {
    if (!draft.publisherTabId?.trim() || !draft.name?.trim()) return;
    if (idError || nameError) return;

    const tabPayload: TabsItem = {
      _id: draft.publisherTabId,
      publisherTabId: draft.publisherTabId,
      name: draft.name
    };

    if (editedTabId) {
      const prevRow = (values.tabs || []).find(
        (tab) => tab.publisherTabId === editedTabId
      );
      if (prevRow?.tabId) {
        tabPayload.tabId = prevRow.tabId;
      }
      if (prevRow?.toAdd) {
        tabPayload.toAdd = true;
      } else {
        tabPayload.toBeEdited = true;
      }

      if (draft.iconUrl) {
        tabPayload.iconUrl = draft.iconUrl;
      } else if (prevRow?.iconUrl) {
        tabPayload.iconUrl = '';
      }

      const updatedTabs = (values.tabs || []).map((tab) =>
        tab.publisherTabId === editedTabId ? tabPayload : tab
      );

      setFieldValue('tabs', updatedTabs);
      setEditedTabId('');
    } else {
      if (draft.iconUrl) {
        tabPayload.iconUrl = draft.iconUrl;
      }
      tabPayload.toAdd = true;
      setFieldValue('tabs', [...(values.tabs || []), tabPayload]);
    }

    setDraft(emptyDraft);
  }, [draft, editedTabId, idError, nameError, setFieldValue, values.tabs]);

  const reorderTabs = useCallback(
    (reordered: TabsItem[]) => {
      const deleted = (values.tabs || []).filter((t) => t.toBeDeleted);
      setFieldValue('tabs', [...reordered, ...deleted]);
    },
    [setFieldValue, values.tabs]
  );

  const removeRow = useCallback(
    (id: string) => {
      if (id === editedTabId) {
        setEditedTabId('');
        setDraft(emptyDraft);
      }

      const tabs = values.tabs || [];
      const existing = tabs.find(
        (tab) =>
          tab.publisherTabId === id &&
          (Boolean(tab.tabId) || !tab.toAdd)
      );

      const updatedTabs = existing
        ? tabs.map((tab) =>
            tab.publisherTabId === id ? { ...tab, toBeDeleted: true } : tab
          )
        : tabs.filter((tab) => tab.publisherTabId !== id);

      setFieldValue('tabs', updatedTabs);
    },
    [editedTabId, setFieldValue, values.tabs]
  );

  const startEdit = useCallback((row: TabsItem) => {
    setEditedTabId(row.publisherTabId);
    setDraft({
      name: row.name,
      publisherTabId: row.publisherTabId,
      iconUrl: row.iconUrl || ''
    });
  }, []);

  const tabTableColumns = useMemo((): ColumnDef<TabsItem>[] => {
    return [
      {
        id: 'drag',
        header: '',
        size: 48,
        cell: () => null
      },
      {
        id: 'iconUrl',
        header: 'Image',
        cell: ({ row }) => {
          const url = row.original.iconUrl;
          return url ? (
            <img
              src={url}
              alt={`${row.original.name} icon`}
              className="h-10 w-10 rounded object-cover"
            />
          ) : null;
        }
      },
      {
        accessorKey: 'publisherTabId',
        header: 'ID',
        cell: ({ row }) => (
          <span className="block truncate font-sans text-sm text-base-foreground">
            {row.original.publisherTabId}
          </span>
        )
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="block truncate font-sans text-sm text-base-foreground">
            {row.original.name}
          </span>
        )
      },
      {
        id: 'actions',
        header: '',
        size: 48,
        cell: ({ row }) => {
          const id = row.original.publisherTabId;
          return (
            <ActionCell
              testId={`storeSettingsTabMenu-${id}`}
              rowData={row.original}
              actions={[
                {
                  label: 'Edit',
                  handler: (data: TabsItem) => startEdit(data),
                  testId: `storeSettingsTabEdit-${id}`
                },
                {
                  label: 'Delete',
                  handler: (data: TabsItem) => removeRow(data.publisherTabId),
                  testId: `storeSettingsTabRemove-${id}`
                }
              ]}
            />
          );
        }
      }
    ];
  }, [removeRow, startEdit]);

  const addDisabled =
    !draft.publisherTabId?.trim() ||
    !draft.name?.trim() ||
    Boolean(idError) ||
    Boolean(nameError);

  return (
    <div className="min-w-0 [&_input:not([aria-invalid])]:border-zinc-200 [&_input]:placeholder:text-base-mutedForeground">
      <div className="grid grid-cols-1 items-start gap-9 md:grid-cols-2">
        <FormField
          label="Tab Name"
          name="storeSettingsTabNameDraft"
          value={draft.name}
          onChange={(e) =>
            setDraft((d) => ({ ...d, name: e.target.value }))
          }
          placeholder="Name"
          error={Boolean(nameError)}
          helperText={nameError || undefined}
          dataTestId="storeSettingsTabName"
        />
        <FormField
          label="Tab ID"
          name="storeSettingsTabIdDraft"
          value={draft.publisherTabId}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              publisherTabId: e.target.value
            }))
          }
          placeholder="ID"
          error={Boolean(idError)}
          helperText={idError || undefined}
          dataTestId="storeSettingsTabId"
        />
      </div>
      <UploadImage
        title="Icon"
        description="For optimized display, make sure your image is at least 300px with 1:1 aspect ratio"
        buttonText="Upload"
        onUpload={(url) =>
          setDraft((d) => ({ ...d, iconUrl: url }))
        }
        selectedImageUrl={draft.iconUrl}
        forceType={EAssetType.GENERAL}
        variant="storeSettings"
        className="mt-9 w-full"
        data-testid="storeSettingsTabIcon"
        buttonDataTestId="uploadStoreSettingsTabIcon"
      />
      <Button
        type="button"
        variant="outline"
        className="mt-9 h-10 w-full self-stretch items-center justify-center gap-2 rounded-md border-0 bg-base-secondary px-4 py-2 font-sans text-[14px] font-medium text-base-secondaryForeground shadow-none hover:bg-base-secondary"
        disabled={addDisabled}
        onClick={addOrSaveTab}
        data-testid="storeSettingsTabAdd"
      >
        {editedTabId ? (
          'Save Tab'
        ) : (
          <>
            <CirclePlus className="h-5 w-5 text-base-secondaryForeground" />
            Add Tab
          </>
        )}
      </Button>
      {tableRows.length > 0 && (
        <DataTable<TabsItem>
          data={tableRows}
          columns={tabTableColumns}
          rowIdIdentifier="publisherTabId"
          showPagination={false}
          rowsPerPage={500}
          stickyHeader
          enableRowReorder
          onReorder={reorderTabs}
          enableColumnResizing
          dragHandleColumnId="drag"
          className="mt-9 min-w-0"
        />
      )}
    </div>
  );
};
